import React, { useState } from 'react';
import { ArrowRight, Upload, Check, AlertCircle, Plus, Minus, ShoppingCart, Star, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';
import ContactButton from '../components/ContactButton';
import api from '../services/api';

const ADULT_SIZES = ['S', 'M', 'L', 'XL', '2XL', '3XL'];
const YOUTH_SIZES = ['YS', 'YM', 'YL', 'YXL', 'Y2XL'];

const UniformOrder = () => {
  const [uniformType, setUniformType] = useState('standard');
  const [quantity, setQuantity] = useState(11);
  const [customQuantity, setCustomQuantity] = useState('');
  const [players, setPlayers] = useState([]);
  const [needsLogo, setNeedsLogo] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [rushOrder, setRushOrder] = useState(false);
  const [contactInfo, setContactInfo] = useState({ name: '', email: '', phone: '', teamName: '' });
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Initialize players when quantity changes
  React.useEffect(() => {
    const newPlayers = Array.from({ length: quantity }, (_, i) => ({
      id: i,
      name: '',
      number: '',
      size: '',
      sizeCategory: 'adult'
    }));
    setPlayers(newPlayers);
  }, [quantity]);

  const updatePlayer = (id, field, value) => {
    setPlayers(players.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const basePrice = uniformType === 'standard' ? 125 : 165;
  const uniformTotal = basePrice * quantity;
  const logoFee = needsLogo && !logoFile ? 100 : 0;
  const rushFee = rushOrder ? 350 : 0;
  const totalPrice = uniformTotal + logoFee + rushFee;

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/png', 'image/jpeg', 'application/pdf', 'application/postscript'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload PNG, JPG, PDF, AI, or EPS file');
        return;
      }
      if (file.size > 50 * 1024 * 1024) { // 50MB limit
        toast.error('File size must be under 50MB');
        return;
      }
      setLogoFile(file);
      toast.success('Logo uploaded successfully');
    }
  };

  const handleCheckout = async () => {
    // Validate form
    if (!contactInfo.name || !contactInfo.email || !contactInfo.teamName) {
      toast.error('Please fill in all contact information');
      return;
    }

    if (!logoFile && !needsLogo) {
      toast.error('Please upload a logo or select "Need Logo Design"');
      return;
    }

    try {
      setCheckoutLoading(true);

      // Filter out players with info (optional fields)
      const playersWithInfo = players.filter(p => p.name || p.number || p.size);
      
      // Prepare custom attributes for the order
      const customAttributes = [
        { key: 'Team Name', value: contactInfo.teamName },
        { key: 'Contact Name', value: contactInfo.name },
        { key: 'Contact Email', value: contactInfo.email },
        { key: 'Contact Phone', value: contactInfo.phone || 'Not provided' },
        { key: 'Uniform Type', value: uniformType === 'standard' ? 'Standard ($125)' : 'Premium ($165)' },
        { key: 'Quantity', value: quantity.toString() },
        { key: 'Logo Design Needed', value: needsLogo ? 'Yes (+$100)' : 'No' },
        { key: 'Rush Order', value: rushOrder ? 'Yes (+$350)' : 'No' },
        { key: 'Total Price', value: `$${totalPrice}` }
      ];

      // Add player roster if provided
      if (playersWithInfo.length > 0) {
        const rosterInfo = playersWithInfo.map((p, i) => 
          `Player ${i + 1}: ${p.name || 'No name'} #${p.number || 'N/A'} Size: ${p.size || 'N/A'}`
        ).join(' | ');
        customAttributes.push({ key: 'Player Roster', value: rosterInfo });
      }

      // NOTE: This needs a real Shopify product variant ID for custom uniforms
      // For now, we'll create a checkout with a note to contact
      const checkoutData = {
        lineItems: [
          {
            // This should be replaced with an actual custom uniform product variant ID from your Shopify store
            variantId: 'gid://shopify/ProductVariant/CUSTOM_UNIFORM_VARIANT_ID',
            quantity: 1
          }
        ],
        customAttributes: customAttributes
      };

      // Create checkout
      const response = await api.post('/shopify/checkout', checkoutData);
      
      if (response.data.success && response.data.checkout.webUrl) {
        toast.success('Redirecting to checkout...');
        // Redirect to Shopify checkout
        setTimeout(() => {
          window.location.href = response.data.checkout.webUrl;
        }, 1000);
      } else {
        throw new Error('Failed to create checkout');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      
      // Fallback: Show order summary and ask to contact
      toast.error('Unable to process checkout online');
      
      const playersWithInfo = players.filter(p => p.name || p.number || p.size);
      const rosterSummary = playersWithInfo.length > 0 
        ? `\\n\\nPlayer Roster:\\n${playersWithInfo.map((p, i) => `${i+1}. ${p.name || 'No name'} #${p.number || 'N/A'} (${p.size || 'No size'})`).join('\\n')}`
        : '';
      
      setTimeout(() => {
        if (window.confirm(`Order Summary:
Team: ${contactInfo.teamName}
Contact: ${contactInfo.name} (${contactInfo.email})
Uniforms: ${quantity} ${uniformType} uniforms
Logo: ${needsLogo ? 'Design needed (+$100)' : 'Will provide'}
Rush Order: ${rushOrder ? 'Yes (+$350)' : 'No'}
Total: $${totalPrice}${rosterSummary}

We'll contact you at ${contactInfo.email} to complete your order. Would you like to email us now?`)) {
          window.location.href = `mailto:info@prsvr.com?subject=Custom Uniform Order - ${contactInfo.teamName}&body=Team: ${contactInfo.teamName}%0D%0AContact: ${contactInfo.name}%0D%0AEmail: ${contactInfo.email}%0D%0APhone: ${contactInfo.phone}%0D%0AQuantity: ${quantity}%0D%0AType: ${uniformType}%0D%0ATotal: $${totalPrice}`;
        }
      }, 1000);
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-charcoal">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-charcoal/95 backdrop-blur-md border-b border-warmSage/20">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/bcsdxd12_IMG_2797.PNG" 
                alt="PRSVR Logo" 
                className="h-10"
                style={{ filter: 'brightness(0) saturate(100%) invert(98%) sepia(6%) saturate(214%) hue-rotate(327deg) brightness(102%) contrast(96%)' }}
              />
            </a>
            <Button onClick={() => window.location.href = '/'} variant="ghost" className="text-cream">
              Back to Home
            </Button>
          </div>
        </nav>
      </header>

      {/* Hero Section with Animation */}
      <section className="pt-32 pb-16 px-6 bg-gradient-to-b from-charcoal to-black relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="floating-circle-1"></div>
          <div className="floating-circle-2"></div>
          <div className="floating-circle-3"></div>
        </div>
        
        <div className="container mx-auto text-center relative z-10">
          <Badge className="badge-streetwear mb-6 animate-fadeIn">PRSVR SPORTS</Badge>
          <h1 className="text-6xl font-black text-cream uppercase mb-4 tracking-tight animate-slideUp">
            CUSTOM TEAM<br/>UNIFORMS
          </h1>
          <p className="text-xl text-warmGray max-w-2xl mx-auto mb-8 animate-fadeIn" style={{animationDelay: '0.2s'}}>
            Premium custom team uniforms. Fully customizable with your team's logo, player names, and numbers. Built to perform.
          </p>
          <div className="flex justify-center gap-8 text-cream">
            <div className="text-center animate-fadeIn" style={{animationDelay: '0.3s'}}>
              <p className="text-3xl font-black text-warmSage animate-bounce-slow">3-5</p>
              <p className="text-sm uppercase tracking-wider">Weeks Standard</p>
            </div>
            <div className="text-center animate-fadeIn" style={{animationDelay: '0.4s'}}>
              <p className="text-3xl font-black text-warmSage animate-bounce-slow" style={{animationDelay: '0.2s'}}>$125+</p>
              <p className="text-sm uppercase tracking-wider">Per Uniform</p>
            </div>
            <div className="text-center animate-fadeIn" style={{animationDelay: '0.5s'}}>
              <p className="text-3xl font-black text-warmSage animate-bounce-slow" style={{animationDelay: '0.4s'}}>100%</p>
              <p className="text-sm uppercase tracking-wider">Custom</p>
            </div>
          </div>
        </div>
        
        {/* Animated Logo */}
        <div className="mt-12 flex justify-center">
          <div className="animate-pulse-slow">
            <img 
              src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/bcsdxd12_IMG_2797.PNG" 
              alt="PRSVR Logo" 
              className="h-20 opacity-30"
              style={{ filter: 'brightness(0) saturate(100%) invert(98%) sepia(6%) saturate(214%) hue-rotate(327deg) brightness(102%) contrast(96%)' }}
            />
          </div>
        </div>
      </section>
      
      <style jsx>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes bounceSlow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes pulseSlow {
          0%, 100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 0.6;
            transform: scale(1.05);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translate(0, 0) rotate(0deg);
          }
          25% {
            transform: translate(10px, -10px) rotate(5deg);
          }
          50% {
            transform: translate(20px, 10px) rotate(-5deg);
          }
          75% {
            transform: translate(-10px, 20px) rotate(3deg);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out forwards;
        }
        
        .animate-slideUp {
          animation: slideUp 0.8s ease-out forwards;
        }
        
        .animate-bounce-slow {
          animation: bounceSlow 3s ease-in-out infinite;
        }
        
        .animate-pulse-slow {
          animation: pulseSlow 4s ease-in-out infinite;
        }
        
        .floating-circle-1 {
          position: absolute;
          width: 300px;
          height: 300px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(196, 181, 160, 0.1) 0%, transparent 70%);
          top: 10%;
          left: -10%;
          animation: float 20s ease-in-out infinite;
        }
        
        .floating-circle-2 {
          position: absolute;
          width: 400px;
          height: 400px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(196, 181, 160, 0.08) 0%, transparent 70%);
          top: 50%;
          right: -15%;
          animation: float 25s ease-in-out infinite reverse;
        }
        
        .floating-circle-3 {
          position: absolute;
          width: 200px;
          height: 200px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(196, 181, 160, 0.12) 0%, transparent 70%);
          bottom: 10%;
          left: 30%;
          animation: float 18s ease-in-out infinite;
          animation-delay: 2s;
        }
      `}</style>

      {/* The PRSVR Promise */}
      <section className="py-16 px-6 bg-charcoal">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-cream uppercase mb-4">THE PRSVR PROMISE</h2>
            <p className="text-xl text-warmSage font-semibold mt-4">Dream it. Secure it. We protect your investment and bring your team's vision to life.</p>
            <p className="text-warmGray mt-6 text-lg">See what we've created for teams like yours</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 border-warmSage bg-charcoal/50 hover:bg-charcoal transition-all">
              <CardContent className="p-8">
                <div className="h-48 mb-6 rounded overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/6i75oh55_IMG_3286.JPG" 
                    alt="Design and Manufacture"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-2xl font-bold text-cream uppercase mb-4 tracking-tight">DESIGN AND MANUFACTURE</h3>
                <p className="text-warmGray">Before production, we will bring your vision to life for your approval</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-warmSage bg-charcoal/50 hover:bg-charcoal transition-all">
              <CardContent className="p-8">
                <div className="h-48 mb-6 rounded overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/1sayup22_IMG_3287.JPG" 
                    alt="Pictures and Performance"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-2xl font-bold text-cream uppercase mb-4 tracking-tight">PICTURES AND PERFORMANCE</h3>
                <p className="text-warmGray">You'll want to share the success of looking good and playing well.</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-warmSage bg-charcoal/50 hover:bg-charcoal transition-all">
              <CardContent className="p-8">
                <div className="h-48 mb-6 rounded overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/p34awdig_IMG_3285.JPG" 
                    alt="Creativity and Trust"
                    className="w-full h-full object-cover"
                    style={{objectPosition: 'center top'}}
                  />
                </div>
                <h3 className="text-2xl font-bold text-cream uppercase mb-4 tracking-tight">CREATIVITY AND TRUST</h3>
                <p className="text-warmGray">After 13 years in the custom clothing business, we are ready to serve yours!</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Order Form */}
      <section className="py-16 px-6 bg-cream">
        <div className="container mx-auto max-w-4xl">
          <Card className="border-4 border-charcoal bg-white">
            <CardHeader className="bg-charcoal text-cream">
              <CardTitle className="text-3xl font-black uppercase">BUILD YOUR ORDER</CardTitle>
            </CardHeader>
            <CardContent className="p-8 space-y-8 bg-white">
              
              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="text-2xl font-black text-charcoal uppercase mb-4">Contact Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Your Name *</Label>
                    <Input 
                      id="name"
                      value={contactInfo.name}
                      onChange={(e) => setContactInfo({...contactInfo, name: e.target.value})}
                      placeholder="John Smith"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="teamName">Team Name *</Label>
                    <Input 
                      id="teamName"
                      value={contactInfo.teamName}
                      onChange={(e) => setContactInfo({...contactInfo, teamName: e.target.value})}
                      placeholder="Lakers"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input 
                      id="email"
                      type="email"
                      value={contactInfo.email}
                      onChange={(e) => setContactInfo({...contactInfo, email: e.target.value})}
                      placeholder="coach@team.com"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input 
                      id="phone"
                      value={contactInfo.phone}
                      onChange={(e) => setContactInfo({...contactInfo, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                      className="mt-2"
                    />
                  </div>
                </div>
              </div>

              {/* Uniform Type Selection */}
              <div className="space-y-4">
                <h3 className="text-2xl font-black text-charcoal uppercase">Select Uniform Type</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <Card 
                    className={`cursor-pointer transition-all bg-white ${uniformType === 'standard' ? 'border-4 border-warmSage' : 'border-2 border-gray-300'}`}
                    onClick={() => setUniformType('standard')}
                  >
                    <CardContent className="p-6 bg-white">
                      <div className="flex justify-between items-start mb-4">
                        <h4 className="text-xl font-bold">Standard Uniform</h4>
                        <Badge className="bg-warmSage text-charcoal">$125/ea</Badge>
                      </div>
                      <p className="text-sm text-warmGray">Breathable fabric with the fit and finish your team needs to perform.</p>
                      {uniformType === 'standard' && <Check className="h-6 w-6 text-warmSage mt-4" />}
                    </CardContent>
                  </Card>
                  <Card 
                    className={`cursor-pointer transition-all bg-white ${uniformType === 'premium' ? 'border-4 border-warmSage' : 'border-2 border-gray-300'}`}
                    onClick={() => setUniformType('premium')}
                  >
                    <CardContent className="p-6 bg-white">
                      <div className="flex justify-between items-start mb-4">
                        <h4 className="text-xl font-bold">Premium Uniform</h4>
                        <Badge className="bg-warmSage text-charcoal">$165/ea</Badge>
                      </div>
                      <p className="text-sm text-warmGray">Upgraded materials and construction — superior stretch, comfort, and durability.</p>
                      {uniformType === 'premium' && <Check className="h-6 w-6 text-warmSage mt-4" />}
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Quantity Selection */}
              <div className="space-y-4">
                <h3 className="text-2xl font-black text-charcoal uppercase">How Many Uniforms?</h3>
                <div className="max-w-xs">
                  <Select
                    value={quantity.toString()}
                    onValueChange={(value) => setQuantity(parseInt(value))}
                  >
                    <SelectTrigger className="h-14 text-lg font-bold">
                      <SelectValue placeholder="Select quantity" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 15 }, (_, i) => i + 1).map(num => (
                        <SelectItem key={num} value={num.toString()}>
                          {num} {num === 1 ? 'Uniform' : 'Uniforms'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-sm text-warmGray">
                  <AlertCircle className="inline h-4 w-4 mr-1" />
                  Need more than 15 uniforms? <a href="mailto:info@prsvr.com" className="text-warmSage font-bold underline">Request a quote</a>
                </p>
              </div>

              {/* Player Roster - Optional */}
              {players.length > 0 && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-2xl font-black text-charcoal uppercase">Player Roster (Optional)</h3>
                    <p className="text-sm text-warmGray mt-2">Add player names, numbers, and sizes now, or provide them later</p>
                  </div>
                  <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                    {players.map((player, index) => (
                      <div 
                        key={player.id} 
                        className="grid grid-cols-12 gap-3 items-center p-4 bg-softGray rounded border-2 border-gray-200 transition-all duration-300 hover:border-warmSage hover:shadow-lg"
                        style={{
                          animation: `slideIn 0.3s ease-out ${index * 0.05}s backwards`
                        }}
                      >
                        <div className="col-span-1 font-bold text-charcoal">#{index + 1}</div>
                        <div className="col-span-4">
                          <Input 
                            placeholder="Player Name (Optional)"
                            value={player.name}
                            onChange={(e) => updatePlayer(player.id, 'name', e.target.value)}
                          />
                        </div>
                        <div className="col-span-2">
                          <Input 
                            placeholder="Number"
                            value={player.number}
                            onChange={(e) => updatePlayer(player.id, 'number', e.target.value)}
                          />
                        </div>
                        <div className="col-span-2">
                          <Select
                            value={player.sizeCategory}
                            onValueChange={(value) => {
                              updatePlayer(player.id, 'sizeCategory', value);
                              updatePlayer(player.id, 'size', '');
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Age" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="adult">Adult</SelectItem>
                              <SelectItem value="youth">Youth</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-3">
                          <Select
                            value={player.size}
                            onValueChange={(value) => updatePlayer(player.id, 'size', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Size" />
                            </SelectTrigger>
                            <SelectContent>
                              {(player.sizeCategory === 'adult' ? ADULT_SIZES : YOUTH_SIZES).map(size => (
                                <SelectItem key={size} value={size}>{size}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Logo Upload */}
              <div className="space-y-4">
                <h3 className="text-2xl font-black text-charcoal uppercase">Team Logo</h3>
                <div className="border-2 border-dashed border-warmGray rounded-lg p-8 text-center">
                  <Upload className="h-12 w-12 text-warmSage mx-auto mb-4" />
                  <Label htmlFor="logo-upload" className="cursor-pointer">
                    <div className="text-lg font-bold text-charcoal mb-2">
                      {logoFile ? logoFile.name : 'Upload Your Logo'}
                    </div>
                    <p className="text-sm text-warmGray mb-4">
                      Accepted formats: PNG, JPG, PDF, AI, EPS<br/>
                      Max size: 50MB | Minimum resolution: 300 DPI<br/>
                      Vector formats (AI, EPS, PDF) preferred for best quality
                    </p>
                    <Button type="button" className="btn-dark">
                      Choose File
                    </Button>
                  </Label>
                  <Input 
                    id="logo-upload"
                    type="file"
                    accept=".png,.jpg,.jpeg,.pdf,.ai,.eps"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="needs-logo"
                    checked={needsLogo}
                    onCheckedChange={setNeedsLogo}
                  />
                  <Label htmlFor="needs-logo" className="cursor-pointer">
                    I need logo design services (+$100)
                  </Label>
                </div>
              </div>

              {/* Rush Order */}
              <div className="space-y-4">
                <div className="flex items-center justify-between p-6 bg-cream rounded-lg border-2 border-warmSage">
                  <div className="flex items-center space-x-3">
                    <Checkbox 
                      id="rush-order"
                      checked={rushOrder}
                      onCheckedChange={setRushOrder}
                    />
                    <div>
                      <Label htmlFor="rush-order" className="text-lg font-bold cursor-pointer">Rush Order - 10-12 Days</Label>
                      <p className="text-sm text-warmGray">Get your uniforms in 10-12 business days instead of standard 3-5 weeks</p>
                    </div>
                  </div>
                  <Badge className="bg-warmSage text-charcoal text-lg">+$350</Badge>
                </div>
                <p className="text-sm text-warmGray text-center">
                  <AlertCircle className="inline h-4 w-4 mr-1" />
                  Need it sooner? <button onClick={() => document.querySelector('.fixed.bottom-8 button')?.click()} className="text-warmSage font-bold underline hover:text-warmSage/80">Contact us</button> to discuss expedited options
                </p>
              </div>

              {/* Price Summary */}
              <div className="space-y-4 p-6 bg-charcoal text-cream rounded-lg">
                <h3 className="text-2xl font-black uppercase">Order Summary</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>{quantity} × {uniformType === 'standard' ? 'Standard' : 'Premium'} Uniforms</span>
                    <span className="font-bold">${uniformTotal}</span>
                  </div>
                  {needsLogo && !logoFile && (
                    <div className="flex justify-between">
                      <span>Logo Design</span>
                      <span className="font-bold">${logoFee}</span>
                    </div>
                  )}
                  {rushOrder && (
                    <div className="flex justify-between">
                      <span>Rush Order (5 days)</span>
                      <span className="font-bold">${rushFee}</span>
                    </div>
                  )}
                  <div className="border-t border-warmSage/30 pt-2 mt-2">
                    <div className="flex justify-between text-2xl">
                      <span className="font-black">TOTAL</span>
                      <span className="font-black text-warmSage">${totalPrice}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Checkout Button */}
              <Button 
                onClick={handleCheckout}
                disabled={checkoutLoading}
                className="w-full h-16 text-lg font-black bg-warmSage text-charcoal hover:bg-warmSage/90 disabled:opacity-50"
              >
                <ShoppingCart className="mr-2 h-6 w-6" />
                {checkoutLoading ? 'PROCESSING...' : 'PROCEED TO CHECKOUT'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6 bg-charcoal">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-cream uppercase mb-4">FREQUENTLY ASKED QUESTIONS</h2>
          </div>
          <Accordion type="single" collapsible>
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-cream font-bold">What's the turnaround time?</AccordionTrigger>
              <AccordionContent className="text-warmGray">
                Standard orders take 3-5 weeks to complete. Rush orders (additional $350) can be completed in 10-12 business days. Need it even sooner? Contact us to discuss expedited options.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger className="text-cream font-bold">What logo formats do you accept?</AccordionTrigger>
              <AccordionContent className="text-warmGray">
                We accept PNG, JPG, PDF, AI, and EPS files. Vector formats (AI, EPS, PDF) are preferred for best quality. Files should be at least 300 DPI and under 50MB.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger className="text-cream font-bold">Can I order more than 15 uniforms?</AccordionTrigger>
              <AccordionContent className="text-warmGray">
                Yes! For orders over 15 uniforms, please contact us at info@prsvr.com for a custom quote and potential volume discounts.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4">
              <AccordionTrigger className="text-cream font-bold">What's included in the logo design service?</AccordionTrigger>
              <AccordionContent className="text-warmGray">
                Our $100 logo design service includes 2 initial concepts, 1 round of revisions, and final files in all necessary formats for uniform printing.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-5">
              <AccordionTrigger className="text-cream font-bold">What's the difference between Standard and Premium?</AccordionTrigger>
              <AccordionContent className="text-warmGray">
                Standard uniforms feature breathable performance fabric ideal for ventilation and high-output play. Premium uniforms step up with superior stretch, enhanced durability, a tailored fit, and an elevated finish — built for teams that want the top-shelf product.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-6">
              <AccordionTrigger className="text-cream font-bold">Do you offer samples?</AccordionTrigger>
              <AccordionContent className="text-warmGray">
                Yes! Samples are available for $200 per single uniform. Contact us at info@prsvr.com to request a sample or discuss your specific needs before placing a full team order.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-7">
              <AccordionTrigger className="text-cream font-bold">What is your refund policy?</AccordionTrigger>
              <AccordionContent className="text-warmGray">
                <strong>No Refunds on Custom Orders:</strong> All custom uniform orders are final sale and non-refundable once production begins. We carefully review all order details including player names, numbers, sizes, and logo placement before production. 
                <br/><br/>
                <strong>Customer Responsibility:</strong> It is the customer's responsibility to verify all information (spelling, numbers, sizes, logo files) is correct before final approval. PRSVR is not responsible for errors in customer-provided information including but not limited to: incorrect player names/numbers, wrong sizes selected, low-quality logos, or missing order details.
                <br/><br/>
                <strong>Manufacturing Defects Only:</strong> We will replace or repair uniforms only in cases of verified manufacturing defects or errors made by PRSVR during production. This does not include customer ordering errors, size dissatisfaction, or design preferences.
                <br/><br/>
                <strong>Order Review:</strong> Customers will receive an order confirmation for review before production starts. Please carefully review all details within 48 hours of receiving confirmation.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-8">
              <AccordionTrigger className="text-cream font-bold">What is your pricing and inventory policy?</AccordionTrigger>
              <AccordionContent className="text-warmGray">
                <strong>Our Pricing and Inventory Policy:</strong> Most of our items are kept in stock for quick delivery. However, for certain newer models or items that require specialized production, we may operate on a made-to-order basis.
                <br/><br/>
                When an item is made-to-order, it is created specifically after you place your order. This process may require a lead time of up to 3 weeks for delivery.
                <br/><br/>
                <strong>Benefits to You:</strong>
                <br/><br/>
                <strong>1. Stable Pricing and Value:</strong> By eliminating the risk of unclaimed inventory on specialized items, we avoid frequent clearance sales. When you purchase one of these pieces, you can trust that you are getting it at a consistent, fair price, and its original value is protected—you won't see it marked down significantly just a week later.
                <br/><br/>
                <strong>2. Exclusivity and Uniqueness:</strong> The made-to-order process significantly reduces the chance that you will encounter others with the exact same newer or specialized item, especially at a special occasion. This helps ensure your purchase remains unique.
                <br/><br/>
                <a href="/pricing-policy" className="text-warmSage hover:underline font-bold">View Full Pricing Policy →</a>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-6 bg-softGray">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-charcoal uppercase mb-4">WHAT TEAMS ARE SAYING</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote: "PRSVR delivered top-quality uniforms on time. Our team looks professional and the fit is perfect. Highly recommend!",
                author: "Coach Martinez",
                team: "Central High Basketball"
              },
              {
                quote: "The customization options were exactly what we needed. Rush order came through and saved our season opener!",
                author: "Director Williams",
                team: "Elite Youth League"
              },
              {
                quote: "Best uniform investment we've made. The premium material is incredibly comfortable and the logo came out sharp.",
                author: "Team Captain Johnson",
                team: "Metro United"
              }
            ].map((testimonial, i) => (
              <Card key={i} className="border-2 border-charcoal bg-white">
                <CardContent className="p-8 bg-white">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-warmSage text-warmSage" />
                    ))}
                  </div>
                  <p className="text-charcoal mb-6 italic">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-bold text-charcoal">{testimonial.author}</p>
                    <p className="text-sm text-warmGray">{testimonial.team}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Button */}
      <ContactButton />

      {/* Footer CTA */}
      <section className="py-16 px-6 bg-warmSage text-charcoal">
        <div className="container mx-auto text-center">
          <h2 className="text-5xl font-black uppercase mb-4">STILL HAVE QUESTIONS?</h2>
          <p className="text-xl mb-8">Our team is here to help you create the perfect uniforms</p>
          <Button 
            onClick={() => window.location.href = 'mailto:info@prsvr.com'}
            className="bg-charcoal text-cream hover:bg-charcoal/90 h-14 px-12 text-lg font-bold"
          >
            CONTACT US
          </Button>
        </div>
      </section>
    </div>
  );
};

export default UniformOrder;
